import React, { useState, useEffect } from "react";
import { TextField, Autocomplete } from "@mui/material";
import AxiosInstance from "../../utils/Axios.ts";

interface Location {
  id: number;
  name: string;
}

interface LocationInputProps {
  value: number | null;
  onChange: (value: Location | null) => void;
  label?: string;
  size?: "small" | "medium";
}

const LocationInput: React.FC<LocationInputProps> = ({ value, onChange, label = "Location", size = "medium" }) => {
  const [options, setOptions] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchLocations = async (query: string) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ search: query });
      const response = await AxiosInstance.get(`/locations/?${params.toString()}`);

      if (response.status === 200) {
        setOptions(response.data.results || []);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations(""); // Initial fetch with no query
  }, []);

  const handleInputChange = (event: React.ChangeEvent<{}>, newInputValue: string) => {
    fetchLocations(newInputValue);
  };

  const handleChange = (event: React.ChangeEvent<{}>, newValue: Location | null) => {
    if (newValue) {
      onChange(newValue);
    } else {
      onChange(null);
    }
  };

  return (
    <Autocomplete
      size={size}
      options={options}
      getOptionLabel={(option) => option.name}
      value={options.find((opt) => opt.id === value) || null}
      onInputChange={handleInputChange}
      onChange={handleChange}
      loading={isLoading}
      renderInput={(params) => (
        <TextField {...params} label={label} variant="outlined" fullWidth size={size} />
      )}
    />
  );
};

export default LocationInput;